'use client';

import { IMyAccountLogin } from '@/.generated';
import { desktopHeaderVariants } from '../Header.styles';
import { getCSLPAttributes } from '@/utils/type-guards';
import RichTextWrapper from '@/helpers/Wrappers/RichTextWrapper/RichTextWrapper';
import Link from 'next/link';
import ImageWrapper from '@/helpers/Wrappers/ImageWrapper/ImageWrapper';
import { toEnhancedImage } from '@/utils/image-utils';
import { useGlobalLabels } from '@/context/GlobalLabelContext';
import { useAccountLogin } from '@/lib/hooks/useAccountLogin';
import { trackAdobeClick } from '@/utils/adobe/click';
import { buttonVariants } from '@/helpers/Wrappers/ButtonWrapper/ButtonWrapper.styles';
import { MY_ACCOUNT_LOGIN_URL } from '@/utils/Constants';

type HeaderLoginSectionProps = {
  myAccountLogin?: IMyAccountLogin;
  showAccountLogin?: boolean;
};

export const HeaderLoginSection = ({
  myAccountLogin,
  showAccountLogin,
}: HeaderLoginSectionProps) => {
  const userLoggedIn = useAccountLogin();

  const { globalLabels } = useGlobalLabels();

  //Return early if showAccountLogin is false or myAccountLogin is undefined
  if (!showAccountLogin || !myAccountLogin) return null;

  const {
    loginSection,
    loginContainer,
    loginGrid,
    loginFormColumn,
    loginForm,
    loginFormRow,
    loginFormLabel,
    loginUsernameInput,
    loginPasswordWrapper,
    loginPasswordInput,
    loginForgotPasswordLink,
    loginSubmitButton,
    logoutButton,
    loginSignupColumn,
    loginDisclaimerContainer,
    loginDisclaimerWrapper,
    loginLoggedInRow,
    loginLoggedInInfo,
    loginLoggedInIconWrapper,
    loginLoggedInIcon,
    loginLoggedInActions,
  } = desktopHeaderVariants({
    userLoggedIn,
  });

  const loginButtonBase = buttonVariants({
    variant: myAccountLogin?.login_button?.cta_variant ?? 'primary',
    size: 'variable',
    class: loginSubmitButton(),
  });

  return (
    <div className={loginSection()}>
      <div className={loginContainer()}>
        <div className={loginGrid()}>
          {!userLoggedIn && (
            <>
              <div className={loginFormColumn()}>
                <form
                  data-key="loginForm"
                  id="menu-login"
                  action={myAccountLogin.login_button?.login_link?.href}
                  method="post"
                  className={loginForm()}
                  onSubmit={(e) =>
                    trackAdobeClick(
                      {
                        clickName: myAccountLogin.login_button?.login_link?.title ?? 'Login',
                        clickUrl: MY_ACCOUNT_LOGIN_URL,
                      },
                      e
                    )
                  }
                >
                  <div className={loginFormRow()}>
                    <span
                      className={loginFormLabel()}
                      {...getCSLPAttributes(myAccountLogin.$?.login_form_label)}
                    >
                      {myAccountLogin.login_form_label}
                    </span>
                    <input
                      type="text"
                      id="txtUserName"
                      name="UserName"
                      autoComplete="username"
                      placeholder={myAccountLogin.username_field_placeholder}
                      className={loginUsernameInput()}
                      {...getCSLPAttributes(myAccountLogin.$?.username_field_placeholder)}
                    />
                    <div className={loginPasswordWrapper()}>
                      <input
                        type="password"
                        id="txtPassword"
                        name="Password"
                        autoComplete="current-password"
                        placeholder={myAccountLogin.password_field_placeholder}
                        className={loginPasswordInput()}
                        {...getCSLPAttributes(myAccountLogin.$?.password_field_placeholder)}
                      />
                      <Link
                        href={myAccountLogin.forgot_password_link?.href || '#'}
                        className={loginForgotPasswordLink()}
                        onClick={(e) =>
                          trackAdobeClick(
                            {
                              clickName:
                                myAccountLogin.forgot_password_link?.title ?? 'Forgot Password',
                            },
                            e
                          )
                        }
                        {...getCSLPAttributes(myAccountLogin.$?.forgot_password_link)}
                      >
                        {myAccountLogin.forgot_password_link?.title}
                      </Link>
                    </div>
                    <input
                      type="submit"
                      id="loginSubmit"
                      className={loginButtonBase}
                      value={myAccountLogin.login_button?.login_link?.title}
                      {...getCSLPAttributes(myAccountLogin.$?.login_button)}
                    />

                  </div>
                </form>
              </div>
              <div className={loginSignupColumn()}>
                <RichTextWrapper
                  content={myAccountLogin.sign_up_content}
                  className="headerMyLoginSignupRTE"
                  {...getCSLPAttributes(myAccountLogin.$?.sign_up_content)}
                />
              </div>
            </>
          )}
          {userLoggedIn && (
            <div className={loginLoggedInRow()}>
              <div className={loginLoggedInInfo()}>
                {globalLabels.logged_in_check_icon?.url && (
                  <div className={loginLoggedInIconWrapper()}>
                    <ImageWrapper
                      image={toEnhancedImage(globalLabels.logged_in_check_icon, {
                        fallbackWidth: 50,
                        fallbackHeight: 50,
                        cslp: globalLabels.$?.logged_in_check_icon,
                      })}
                      alt={globalLabels.logged_in_check_icon.title ?? ''}
                      wrapperClassName="w-full h-full"
                      imageClassName={loginLoggedInIcon()}
                    />
                  </div>
                )}
                <RichTextWrapper
                  content={myAccountLogin.logged_in_content}
                  parentClassName="headerLoggedInContent"
                  cslpAttribute={myAccountLogin.$?.logged_in_content}
                />
              </div>
              <div className={loginLoggedInActions()}>
                <Link
                  href={myAccountLogin.logout_link?.href ?? ''}
                  className={logoutButton()}
                  onClick={(e) =>
                    trackAdobeClick({ clickName: myAccountLogin.logout_link?.title ?? 'Logout' }, e)
                  }
                  {...getCSLPAttributes(myAccountLogin.$?.logout_link)}
                >
                  {myAccountLogin.logout_link?.title}
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
      {!userLoggedIn && (
        <div className={loginDisclaimerContainer()}>
          <div className={loginDisclaimerWrapper()}>
            <RichTextWrapper
              content={myAccountLogin.disclaimer_note}
              className="headerMyLoginDisclaimerRTE"
              {...getCSLPAttributes(myAccountLogin.$?.disclaimer_note)}
            />
          </div>
        </div>
      )}
    </div>
  );
};
